/**
 * ============================================================================
 * USER / TENANT PAGE: Support Tickets & Alerts (Sheets 30 & 32)
 * ============================================================================
 * Purpose:
 *   Tenant workspace interface for creating and managing helpdesk tickets
 *   (`tbl_support_tickets`) and viewing platform notifications (`tbl_notifications`).
 *
 * Database Scope:
 *   Tenant Database (`digi_user_<id>`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from "react";
import { ticketService } from "../../services";

// SVG Icons matching design
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
    />
  </svg>
);

const FunnelIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
    />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

const LifebuoyIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
    />
  </svg>
);

const BellIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
    />
  </svg>
);

export default function SupportTickets() {
  const [activeTab, setActiveTab] = useState("tickets"); // 'tickets' | 'alerts'

  // Tickets State
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketLimit, setTicketLimit] = useState(10);
  const [ticketSearch, setTicketSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Alerts State
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [alertPage, setAlertPage] = useState(1);
  const [alertLimit, setAlertLimit] = useState(10);
  const [alertSearch, setAlertSearch] = useState("");

  // Create Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    str_subject: "",
    str_description: "",
    str_priority: "MEDIUM",
  });

  const loadTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const res = await ticketService.get({ limit: 500 });
      if (res && res.success) {
        setTickets(Array.isArray(res.data) ? res.data : []);
      } else if (Array.isArray(res)) {
        setTickets(res);
      }
    } catch (err) {
      console.error("Failed to load tickets:", err);
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const res = await ticketService.notifications.get({ limit: 500 });
      if (res && res.success) {
        setAlerts(Array.isArray(res.data) ? res.data : []);
      } else if (Array.isArray(res)) {
        setAlerts(res);
      }
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setAlerts([]);
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    loadAlerts();
  }, [loadTickets, loadAlerts]);

  const handleRefresh = async () => {
    await Promise.all([loadTickets(), loadAlerts()]);
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await ticketService.create({
        ...ticketForm,
        str_status: "OPEN",
      });
      if (res && (res.success || res._id)) {
        setIsModalOpen(false);
        setTicketForm({ str_subject: "", str_description: "", str_priority: "MEDIUM" });
        await loadTickets();
      } else {
        alert(res?.error || "Failed to create ticket");
      }
    } catch (err) {
      alert(err.message || "Failed to create ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter Tickets
  const getFilteredTickets = () => {
    let result = tickets;
    if (ticketSearch) {
      const q = ticketSearch.toLowerCase();
      result = result.filter(
        (t) =>
          (t.str_ticket_id || "").toLowerCase().includes(q) ||
          (t.str_subject || "").toLowerCase().includes(q) ||
          (t.str_description || "").toLowerCase().includes(q)
      );
    }
    if (priorityFilter !== "all") {
      result = result.filter(
        (t) => (t.str_priority || "MEDIUM").toUpperCase() === priorityFilter.toUpperCase()
      );
    }
    if (statusFilter !== "all") {
      result = result.filter(
        (t) => (t.str_status || "OPEN").toUpperCase() === statusFilter.toUpperCase()
      );
    }
    return result;
  };

  // Filter Alerts
  const getFilteredAlerts = () => {
    let result = alerts;
    if (alertSearch) {
      const q = alertSearch.toLowerCase();
      result = result.filter(
        (a) =>
          (a.str_title || "").toLowerCase().includes(q) ||
          (a.str_message || "").toLowerCase().includes(q)
      );
    }
    return result;
  };

  const filteredTickets = getFilteredTickets();
  const totalTicketPages = Math.ceil(filteredTickets.length / ticketLimit) || 1;
  const startTicketIdx = (ticketPage - 1) * ticketLimit;
  const paginatedTickets = filteredTickets.slice(startTicketIdx, startTicketIdx + ticketLimit);

  const filteredAlerts = getFilteredAlerts();
  const totalAlertPages = Math.ceil(filteredAlerts.length / alertLimit) || 1;
  const startAlertIdx = (alertPage - 1) * alertLimit;
  const paginatedAlerts = filteredAlerts.slice(startAlertIdx, startAlertIdx + alertLimit);

  // Stats calculation
  const totalTickets = tickets.length;
  const openTickets = tickets.filter(
    (t) => (t.str_status || "OPEN").toUpperCase() === "OPEN"
  ).length;
  const resolvedTickets = tickets.filter((t) =>
    ["RESOLVED", "CLOSED"].includes((t.str_status || "").toUpperCase())
  ).length;
  const totalAlerts = alerts.length;

  return (
    <div className="space-y-6 mx-auto pb-12">
      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              Support & Alerts
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Submit inquiries regarding WABA account verification, sender registration, or system notifications
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-sm shadow-green-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Open New Ticket
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
            {totalTickets}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Total Inquiries
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-amber-500 font-mono">
            {openTickets}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Pending Review
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600 font-mono">
            {resolvedTickets}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Resolved Tickets
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-blue-600 font-mono">
            {totalAlerts}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Active Alerts
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-black/5 dark:border-white/10 pb-2">
        <button
          onClick={() => setActiveTab("tickets")}
          className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "tickets"
              ? "bg-[#007AFF] text-white shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          🎫 Support Tickets (tbl_support_tickets)
        </button>
        <button
          onClick={() => setActiveTab("alerts")}
          className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "alerts"
              ? "bg-[#007AFF] text-white shadow-sm"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          🔔 System Notifications (tbl_notifications)
        </button>
      </div>

      {/* Tab 1: Tickets */}
      {activeTab === "tickets" && (
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
              <div className="relative flex-1 min-w-[240px]">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tickets by subject, ticket ID..."
                  value={ticketSearch}
                  onChange={(e) => {
                    setTicketSearch(e.target.value);
                    setTicketPage(1);
                  }}
                  className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="relative">
                <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    setPriorityFilter(e.target.value);
                    setTicketPage(1);
                  }}
                  className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="all">All Priorities</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>

              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setTicketPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="all">All Status</option>
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>

              <div className="relative">
                <select
                  value={ticketLimit}
                  onChange={(e) => {
                    setTicketLimit(Number(e.target.value));
                    setTicketPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tickets Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {paginatedTickets.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No Support Tickets Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
                  {tickets.length === 0
                    ? "You don't have any support inquiries open. Create a ticket if you need assistance."
                    : "No tickets match your current filters."}
                </p>
                {tickets.length === 0 && (
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 text-sm cursor-pointer"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Open New Ticket
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/60">
                      <tr>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">
                          #
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Subject & Details
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Opened On
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedTickets.map((row, index) => {
                        const ticketId = row.str_ticket_id || `TCK_${index + 1}`;
                        const subject = row.str_subject || "General Inquiry";
                        const description = row.str_description || "No description provided";
                        const priority = (row.str_priority || "MEDIUM").toUpperCase();
                        const status = (row.str_status || "OPEN").toUpperCase();
                        const dateStr = row.dttm_created_on
                          ? new Date(row.dttm_created_on).toLocaleDateString()
                          : "-";

                        return (
                          <tr
                            key={row._id || index}
                            className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                              #{(ticketPage - 1) * ticketLimit + index + 1}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-start space-x-3.5">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-center font-bold shadow-sm flex-shrink-0 mt-0.5">
                                  <LifebuoyIcon className="w-5 h-5" />
                                </div>
                                <div className="max-w-md">
                                  <div className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
                                    {subject}
                                  </div>
                                  <div className="text-[11px] font-mono text-[#007AFF] mt-0.5">
                                    ID: {ticketId}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                    {description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {priority === "URGENT" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                                  🔥 URGENT
                                </span>
                              ) : priority === "HIGH" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
                                  ⚠️ HIGH
                                </span>
                              ) : priority === "LOW" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                  LOW
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                  MEDIUM
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {status === "OPEN" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                  ● OPEN
                                </span>
                              ) : status === "RESOLVED" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                  ✓ RESOLVED
                                </span>
                              ) : status === "IN_PROGRESS" ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                  ⚙️ IN PROGRESS
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                  CLOSED
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-xs text-gray-500 dark:text-gray-400">
                              {dateStr}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalTicketPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Showing {(ticketPage - 1) * ticketLimit + 1} to{" "}
                      {Math.min(ticketPage * ticketLimit, filteredTickets.length)} of{" "}
                      {filteredTickets.length} tickets
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setTicketPage((p) => Math.max(1, p - 1))}
                        disabled={ticketPage === 1}
                        className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeftIcon className="w-4 h-4" />
                      </button>

                      {Array.from({ length: Math.min(5, totalTicketPages) }, (_, i) => {
                        const pageNum = i + Math.max(1, ticketPage - 2);
                        if (pageNum > totalTicketPages) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setTicketPage(pageNum)}
                            className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                              ticketPage === pageNum
                                ? "bg-green-600 text-white"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setTicketPage((p) => Math.min(totalTicketPages, p + 1))}
                        disabled={ticketPage === totalTicketPages}
                        className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRightIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: In-App Alerts */}
      {activeTab === "alerts" && (
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
              <div className="relative flex-1 min-w-[240px]">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={alertSearch}
                  onChange={(e) => {
                    setAlertSearch(e.target.value);
                    setAlertPage(1);
                  }}
                  className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="relative">
                <select
                  value={alertLimit}
                  onChange={(e) => {
                    setAlertLimit(Number(e.target.value));
                    setAlertPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
          </div>

          {/* Alerts Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {paginatedAlerts.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No Alerts Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto text-sm">
                  You do not have any new platform notifications or system alerts.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/60">
                      <tr>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">
                          #
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Notification
                        </th>
                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Received
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedAlerts.map((row, index) => {
                        const title = row.str_title || "System Notification";
                        const message = row.str_message || "No content";
                        const type = (row.str_type || "INFO").toUpperCase();
                        const dateStr = row.dttm_created_on
                          ? new Date(row.dttm_created_on).toLocaleString()
                          : "-";

                        return (
                          <tr
                            key={row._id || index}
                            className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                              #{(alertPage - 1) * alertLimit + index + 1}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-start space-x-3.5">
                                <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-[#007AFF] flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                                  <BellIcon className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
                                    {title}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {message}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                                {type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-xs text-gray-500 dark:text-gray-400">
                              {dateStr}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalAlertPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Showing {(alertPage - 1) * alertLimit + 1} to{" "}
                      {Math.min(alertPage * alertLimit, filteredAlerts.length)} of{" "}
                      {filteredAlerts.length} alerts
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setAlertPage((p) => Math.max(1, p - 1))}
                        disabled={alertPage === 1}
                        className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeftIcon className="w-4 h-4" />
                      </button>

                      {Array.from({ length: Math.min(5, totalAlertPages) }, (_, i) => {
                        const pageNum = i + Math.max(1, alertPage - 2);
                        if (pageNum > totalAlertPages) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setAlertPage(pageNum)}
                            className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                              alertPage === pageNum
                                ? "bg-green-600 text-white"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setAlertPage((p) => Math.min(totalAlertPages, p + 1))}
                        disabled={alertPage === totalAlertPages}
                        className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRightIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Open New Support Ticket
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateTicket} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Subject *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Assistance with Meta embedded signup"
                    value={ticketForm.str_subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, str_subject: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Priority
                  </label>
                  <select
                    value={ticketForm.str_priority}
                    onChange={(e) => setTicketForm({ ...ticketForm, str_priority: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Description *
                  </label>
                  <textarea
                    rows={4}
                    required
                    placeholder="Please provide full details of your inquiry or error code..."
                    value={ticketForm.str_description}
                    onChange={(e) => setTicketForm({ ...ticketForm, str_description: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-6 py-2 rounded-xl transition-all flex items-center gap-2 text-xs font-semibold shadow-sm shadow-green-600/20 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      Submit Ticket
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
